import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, photoSrc, rupiah } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import UserPill from "@/components/UserPill";
import { toast } from "sonner";

const TABS = [
  { key: "menu", label: "Menu & Kuota" },
  { key: "pesanan", label: "Pesanan" },
  { key: "lapak", label: "Lapak" },
  { key: "jadwal", label: "Jadwal & Titik Ambil" },
  { key: "performa", label: "Performa" },
  { key: "belanja", label: "Belanja Bahan AI" },
  { key: "tagihan", label: "Tagihan" },
  { key: "admin", label: "Admin", adminOnly: true },
];

const STATUS_LABEL = { baru: "Baru masuk", dikonfirmasi: "Dikonfirmasi", lunas: "Lunas" };
const STATUS_COLOR = { baru: "#E8823A", dikonfirmasi: "#1A17E0", lunas: "#2F7A3D" };

const initials = (n) => (n || "?").split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();

export default function Dashboard() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("menu");
  const [dishes, setDishes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [profile, setProfile] = useState({ open_hour: "07.00", close_hour: "22.00", closed_dates: [] });
  const [pickups, setPickups] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [shopping, setShopping] = useState(null);
  const [orderFilter, setOrderFilter] = useState("all");

  const loadAll = async () => {
    const [a, b, c, d] = await Promise.all([
      api.get("/my/dishes"), api.get("/orders/incoming"),
      api.get("/my/seller-profile"), api.get("/my/pickup-points"),
    ]);
    setDishes(a.data); setOrders(b.data); setProfile(c.data); setPickups(d.data);
  };
  useEffect(() => { loadAll(); }, []);
  useEffect(() => { const t = setInterval(() => api.get("/orders/incoming").then(({ data }) => setOrders(data)).catch(() => {}), 15000); return () => clearInterval(t); }, []);

  const baruCount = orders.filter((o) => o.status === "baru").length;

  const toggleShop = async () => {
    const { data } = await api.put("/my/leave", { on_leave: !user?.on_leave });
    setUser(data);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#FBEFD3", color: "#26221E", fontFamily: "'Nunito Sans', sans-serif" }}>
      {/* HEADER */}
      <header style={{ position: "sticky", top: 0, zIndex: 30, background: "#FBEFD3", borderBottom: "1px solid rgba(38,34,30,0.10)", padding: "22px clamp(20px, 4vw, 56px)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <span data-testid="logo-static" style={{ fontFamily: "'Bitter', Georgia, serif", fontWeight: 800, fontSize: 34, letterSpacing: "-0.02em", color: "#26221E" }}>Cookingin</span>
          <span className="font-mono-c" style={{ fontSize: 12, letterSpacing: "0.18em", color: "#6E6559", padding: "8px 14px", borderRadius: 999, background: "rgba(38,34,30,0.06)" }}>DASHBOARD REKAN MASAK</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button data-testid="shop-toggle" onClick={toggleShop} style={{ padding: "10px 20px", borderRadius: 999, border: user?.on_leave ? "1px solid rgba(38,34,30,0.24)" : "1px solid #1A17E0", background: user?.on_leave ? "transparent" : "#1A17E0", color: user?.on_leave ? "#26221E" : "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>{user?.on_leave ? "Mode Libur" : "Lapak Buka"}</button>
          <UserPill variant="seller" />
        </div>
      </header>

      {/* TAB BAR */}
      <div style={{ position: "sticky", top: 84, zIndex: 20, background: "#FBEFD3", borderBottom: "1px solid rgba(38,34,30,0.10)", padding: "0 clamp(20px, 4vw, 56px)", overflowX: "auto" }}>
        <div style={{ display: "flex", gap: 8, minWidth: "max-content" }}>
          {TABS.filter((t) => !t.adminOnly || user?.is_admin).map((t) => (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)} style={{ padding: "16px 22px", background: "transparent", border: "none", borderBottom: tab === t.key ? "3px solid #1A17E0" : "3px solid transparent", color: tab === t.key ? "#26221E" : "#6E6559", fontWeight: tab === t.key ? 700 : 600, fontSize: 15, cursor: "pointer", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 8 }}>
              {t.label}
              {t.key === "pesanan" && baruCount > 0 && (
                <span style={{ minWidth: 22, height: 22, padding: "0 7px", borderRadius: 999, background: "#E8823A", color: "#fff", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700 }}>{baruCount}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <main style={{ maxWidth: 1560, margin: "0 auto", padding: "clamp(28px, 3vw, 44px) clamp(20px, 4vw, 56px)" }}>
        {tab === "menu"     && <MenuTab dishes={dishes} setDishes={setDishes} profile={profile} />}
        {tab === "pesanan"  && <PesananTab orders={orders} setOrders={setOrders} filter={orderFilter} setFilter={setOrderFilter} />}
        {tab === "lapak"    && <LapakTab userId={user?.user_id} />}
        {tab === "jadwal"   && <JadwalTab profile={profile} setProfile={setProfile} pickups={pickups} setPickups={setPickups} />}
        {tab === "performa" && <PerformaTab analytics={analytics} setAnalytics={setAnalytics} />}
        {tab === "belanja"  && <BelanjaTab shopping={shopping} setShopping={setShopping} />}
        {tab === "tagihan"  && <TagihanTab />}
        {tab === "admin"    && <AdminTab />}
      </main>
    </div>
  );
}

/* ---------------- Tab 1: Menu & Kuota ---------------- */
const EMPTY_DISH = { name: "", category: "", ingredients: "", price: "", photo_url: "", portions: 10 };
function MenuTab({ dishes, setDishes, profile }) {
  const [form, setForm] = useState(EMPTY_DISH);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try { const { data } = await api.post("/upload", fd); setForm((f) => ({ ...f, photo_url: data.photo_url })); }
    catch (e) { toast.error("Gagal upload foto"); }
    finally { setUploading(false); }
  };

  const publish = async () => {
    if (!form.name || !form.photo_url) return toast.error("Foto dan nama wajib diisi");
    const body = { ...form, price: parseInt(String(form.price).replace(/\D/g, "")) || 0, portions: parseInt(form.portions) || 1 };
    try {
      if (editingId) {
        const { data } = await api.put(`/my/dishes/${editingId}`, body);
        setDishes((prev) => prev.map((d) => (d.dish_id === editingId ? data : d)));
        toast.success("Menu diperbarui");
      } else {
        const { data } = await api.post("/my/dishes", body);
        setDishes((prev) => [data, ...prev]);
        toast.success("Menu terbit untuk besok");
      }
      setForm(EMPTY_DISH); setEditingId(null);
    } catch (e) { toast.error("Gagal simpan"); }
  };

  const edit = (d) => { setForm({ name: d.name, category: d.category, ingredients: d.ingredients, price: d.price, photo_url: d.photo_url, portions: d.portions }); setEditingId(d.dish_id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const del = async (id) => { if (!confirm("Hapus menu?")) return; await api.delete(`/my/dishes/${id}`); setDishes((p) => p.filter((d) => d.dish_id !== id)); };

  if (dishes.length === 0 && !editingId) {
    // partial empty state — keep form
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 400px) minmax(0, 1fr)", gap: 32, alignItems: "start" }}>
      <div style={{ background: "#fff", borderRadius: 14, padding: 24, border: "1px solid rgba(38,34,30,0.10)" }}>
        <h2 style={{ fontFamily: "'Bitter', Georgia, serif", fontWeight: 800, fontSize: 22, margin: "0 0 16px" }}>{editingId ? "Edit Menu" : "Terbitkan Menu Baru"}</h2>
        <label data-testid="menu-photo-drop" style={{ display: "block", padding: 20, border: "2px dashed rgba(38,34,30,0.24)", borderRadius: 10, textAlign: "center", cursor: "pointer", marginBottom: 12 }}>
          {form.photo_url ? <img src={photoSrc(form.photo_url)} alt="" style={{ maxWidth: "100%", maxHeight: 180, objectFit: "cover", borderRadius: 6 }} /> : <span style={{ color: "#9A9084", fontSize: 13 }}>{uploading ? "Mengunggah…" : "Klik untuk unggah foto (JPG/PNG, min 1200px)"}</span>}
          <input type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
        </label>
        <input data-testid="menu-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nama Menu" style={inp} />
        <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Kategori (mis. Nasi · Makanan)" style={inp} />
        <textarea value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} placeholder="Deskripsi / isi porsi" style={{ ...inp, minHeight: 70 }} />
        <input data-testid="menu-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Harga (Rp)" style={inp} />
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <span style={{ fontSize: 13, color: "#6E6559" }}>Kuota Maks</span>
          <button onClick={() => setForm({ ...form, portions: Math.max(1, form.portions - 1) })} style={btnStep}>−</button>
          <span data-testid="menu-portions" style={{ fontFamily: "'IBM Plex Mono', monospace", minWidth: 30, textAlign: "center" }}>{form.portions}</span>
          <button onClick={() => setForm({ ...form, portions: Math.min(99, form.portions + 1) })} style={btnStep}>+</button>
        </div>
        <button data-testid="publish-menu" onClick={publish} disabled={uploading} style={{ width: "100%", background: "#1A17E0", color: "#fff", padding: "14px 20px", border: "none", borderRadius: 4, fontWeight: 700, cursor: "pointer" }}>{editingId ? "Simpan Perubahan" : "Publikasikan untuk Besok"}</button>
        <p style={{ marginTop: 10, fontSize: 12, color: "#9A9084" }}>Jam buka {profile.open_hour} — tutup order {profile.close_hour}</p>
      </div>

      <div>
        <p className="font-mono-c" style={{ fontSize: 12, letterSpacing: "0.14em", color: "#6E6559", margin: "0 0 12px" }}>{dishes.length} MENU TAYANG</p>
        {dishes.length === 0 && <div style={emptyBox}>Belum ada menu. Isi form di kiri untuk terbitkan menu pertamamu.</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {dishes.map((d) => {
            const sold = d.sold_count || 0;
            const full = sold >= d.portions;
            const pct = Math.min(100, Math.round((sold / Math.max(d.portions, 1)) * 100));
            return (
              <div key={d.dish_id} data-testid={`menu-row-${d.dish_id}`} style={{ background: "#fff", border: "1px solid rgba(38,34,30,0.10)", borderRadius: 14, padding: 16, display: "grid", gridTemplateColumns: "120px 1fr auto", gap: 16, alignItems: "center" }}>
                <img src={photoSrc(d.photo_url)} alt={d.name} style={{ width: 120, height: 96, objectFit: "cover", borderRadius: 8 }} />
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <strong style={{ fontSize: 16 }}>{d.name}</strong>
                    <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 999, background: full ? "#FDECEA" : "#E7F6EC", color: full ? "#B3261E" : "#2F7A3D", fontWeight: 700 }}>{full ? "Kuota Penuh" : "Tayang"}</span>
                  </div>
                  <p style={{ margin: "4px 0", fontSize: 13, color: "#6E6559" }}>{d.ingredients}</p>
                  <p style={{ margin: "4px 0", fontFamily: "'IBM Plex Mono', monospace", fontSize: 14 }}>{rupiah(d.price)}</p>
                  <div style={{ fontSize: 12, color: "#6E6559", marginTop: 4 }}>terisi {sold}/{d.portions}</div>
                  <div style={{ height: 6, background: "rgba(38,34,30,0.08)", borderRadius: 999, marginTop: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: full ? "#B3261E" : "#1A17E0" }} />
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <button onClick={() => edit(d)} style={btnSm}>Edit</button>
                  <button onClick={() => del(d.dish_id)} style={{ ...btnSm, color: "#B3261E" }}>Hapus</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Tab 2: Pesanan ---------------- */
function PesananTab({ orders, setOrders, filter, setFilter }) {
  const kpi = useMemo(() => {
    const baru = orders.filter((o) => o.status === "baru").length;
    const konf = orders.filter((o) => o.status === "dikonfirmasi");
    const lunas = orders.filter((o) => o.status === "lunas");
    return {
      total: orders.length, baru,
      konf: konf.length, konfRp: konf.reduce((s, o) => s + o.total, 0),
      lunasCount: lunas.length, lunasRp: lunas.reduce((s, o) => s + o.total, 0),
    };
  }, [orders]);

  const filtered = orders.filter((o) => filter === "all" || o.status === filter);

  const advance = async (o) => {
    const endpoint = o.status === "baru" ? "paid" : o.status === "dikonfirmasi" ? "delivered" : null;
    if (!endpoint) return;
    try {
      const { data } = await api.post(`/orders/${o.order_id}/${endpoint}`);
      setOrders((prev) => prev.map((x) => (x.order_id === o.order_id ? data : x)));
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal update"); }
  };

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 20 }}>
        <Kpi label="Pesanan masuk"        value={kpi.total} />
        <Kpi label="Perlu konfirmasi"     value={kpi.baru} color="#E8823A" />
        <Kpi label="Tunggu pembayaran"    value={kpi.konf} color="#1A17E0" sub={rupiah(kpi.konfRp)} />
        <Kpi label="Omzet terkunci"       value={kpi.lunasCount} color="#2F7A3D" sub={rupiah(kpi.lunasRp)} />
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {[["all", "Semua", kpi.total], ["baru", "Perlu konfirmasi", kpi.baru], ["dikonfirmasi", "Tunggu pembayaran", kpi.konf], ["lunas", "Lunas", kpi.lunasCount]].map(([k, l, n]) => (
          <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)} style={{ padding: "8px 14px", borderRadius: 999, border: "1px solid " + (filter === k ? "#1A17E0" : "rgba(38,34,30,0.18)"), background: filter === k ? "#1A17E0" : "#fff", color: filter === k ? "#fff" : "#26221E", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{l} · {n}</button>
        ))}
      </div>
      {filtered.length === 0 && <div style={emptyBox}>Belum ada pesanan pada filter ini.</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {filtered.map((o) => (
          <div key={o.order_id} data-testid={`order-row-${o.order_id}`} style={{ background: "#fff", border: "1px solid rgba(38,34,30,0.10)", borderRadius: 14, padding: 16, display: "grid", gridTemplateColumns: "auto 1fr auto auto auto", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: "#6E6559" }}>{o.order_id}</span>
            <div>
              <strong style={{ fontSize: 14 }}>{o.buyer_name}</strong>
              <div style={{ fontSize: 12, color: "#6E6559", marginTop: 2 }}>{o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}</div>
              {o.note && <div style={{ marginTop: 6, padding: "6px 10px", borderLeft: "3px solid #E8823A", background: "#FFF5EC", fontSize: 12 }}><strong>Catatan:</strong> {o.note}</div>}
            </div>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700 }}>{rupiah(o.total)}</span>
            <span style={{ padding: "4px 10px", borderRadius: 999, background: STATUS_COLOR[o.status] + "1A", color: STATUS_COLOR[o.status], fontSize: 11, fontWeight: 700 }}>{STATUS_LABEL[o.status]}</span>
            {o.status === "baru" && <button data-testid={`konfirmasi-${o.order_id}`} onClick={() => advance(o)} style={{ padding: "10px 16px", background: "#1A17E0", color: "#fff", border: "none", borderRadius: 4, fontWeight: 700, cursor: "pointer" }}>Konfirmasi Pesanan</button>}
            {o.status === "dikonfirmasi" && <button data-testid={`bayar-${o.order_id}`} onClick={() => advance(o)} style={{ padding: "10px 16px", background: "#E8823A", color: "#fff", border: "none", borderRadius: 4, fontWeight: 700, cursor: "pointer" }}>Pembayaran Masuk</button>}
            {o.status === "lunas" && <span className="font-mono-c" style={{ fontSize: 11, color: "#2F7A3D", fontWeight: 700 }}>LUNAS · SIAP ANTAR</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Tab 3: Lapak (identitas + pembayaran + read-only menu) ---------------- */
function LapakTab({ userId }) {
  const [dishes, setDishes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ qris_payload: "", qris_merchant_name: "", bank_name: "", bank_account_name: "", bank_account_number: "" });
  const [saving, setSaving] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    api.get("/dishes").then(({ data }) => setDishes(data));
    api.get("/my/seller-profile").then(({ data }) => {
      setProfile(data);
      // Only seed the form if the seller hasn't started typing (avoid clobbering fast input).
      setForm((prev) => {
        const stillPristine = !prev.qris_payload && !prev.qris_merchant_name && !prev.bank_name && !prev.bank_account_number && !prev.bank_account_name;
        if (!stillPristine) return prev;
        return {
          qris_payload: data.qris_payload || "",
          qris_merchant_name: data.qris_merchant_name || "",
          bank_name: data.bank_name || "",
          bank_account_name: data.bank_account_name || "",
          bank_account_number: data.bank_account_number || "",
        };
      });
      setHydrated(true);
    });
  }, []);

  const looksLikeQris = (s) => (s || "").trim().startsWith("00020101") && (s || "").includes("5802ID");
  const qrisValid = !form.qris_payload || looksLikeQris(form.qris_payload);

  const savePayment = async () => {
    if (!qrisValid) return toast.error("QRIS payload tidak valid — pastikan diawali 00020101 dan berisi 5802ID");
    setSaving(true);
    try {
      const { data } = await api.put("/my/seller-profile", form);
      setProfile(data);
      toast.success("Info pembayaran tersimpan · muncul otomatis di checkout Pemesan");
    } catch (e) { toast.error("Gagal simpan info pembayaran"); }
    finally { setSaving(false); }
  };

  const list = dishes.filter((d) => filter === "all" || d.category?.toLowerCase().includes(filter));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* PAYMENT IDENTITY */}
      <div style={{ background: "#fff", border: "1px solid rgba(38,34,30,0.10)", borderRadius: 14, padding: 24 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 6 }}>
          <div>
            <h3 className="font-display" style={{ margin: 0, fontFamily: "'Bitter', Georgia, serif", fontWeight: 800, fontSize: 22 }}>Identitas Pembayaranmu</h3>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6E6559" }}>Uang langsung masuk ke rekeningmu. Cookingin tidak pernah pegang uangnya.</p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <span data-testid="qris-status" className="font-mono-c" style={{ padding: "6px 12px", borderRadius: 999, fontSize: 11, fontWeight: 700, background: profile?.has_qris ? "#E7F6EC" : "rgba(38,34,30,0.06)", color: profile?.has_qris ? "#2F7A3D" : "#6E6559" }}>QRIS · {profile?.has_qris ? "AKTIF" : "BELUM"}</span>
            <span data-testid="bank-status" className="font-mono-c" style={{ padding: "6px 12px", borderRadius: 999, fontSize: 11, fontWeight: 700, background: profile?.has_bank ? "#E7F6EC" : "rgba(38,34,30,0.06)", color: profile?.has_bank ? "#2F7A3D" : "#6E6559" }}>REKENING · {profile?.has_bank ? "AKTIF" : "BELUM"}</span>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20, marginTop: 16 }}>
          <div>
            <p className="font-mono-c" style={{ fontSize: 11, letterSpacing: "0.14em", color: "#1A17E0", margin: "0 0 6px", fontWeight: 700 }}>QRIS PAYLOAD</p>
            <textarea data-testid="qris-input" value={form.qris_payload} onChange={(e) => setForm({ ...form, qris_payload: e.target.value })} placeholder="Tempel QRIS payload lengkap (00020101...6304XXXX)" style={{ ...inp, minHeight: 96, fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, wordBreak: "break-all", borderColor: qrisValid ? "rgba(38,34,30,0.18)" : "#B3261E" }} />
            {!qrisValid && <p style={{ fontSize: 11, color: "#B3261E", margin: "4px 0 8px" }}>Format QRIS belum tepat. Payload asli diawali 00020101 & memuat 5802ID.</p>}
            <input data-testid="qris-merchant" value={form.qris_merchant_name} onChange={(e) => setForm({ ...form, qris_merchant_name: e.target.value })} placeholder="Nama Merchant di QRIS (mis. WARUNG IBU RINA)" style={inp} />
            <p style={{ margin: "6px 0 0", fontSize: 12, color: "#6E6559" }}>Kami suntik nominal otomatis ke QRIS-mu untuk tiap pesanan sehingga Pemesan tinggal scan & bayar.</p>
          </div>
          <div>
            <p className="font-mono-c" style={{ fontSize: 11, letterSpacing: "0.14em", color: "#E8823A", margin: "0 0 6px", fontWeight: 700 }}>ALTERNATIF TRANSFER BANK</p>
            <input data-testid="bank-name" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="Nama Bank (mis. BCA, BRI, Mandiri)" style={inp} />
            <input data-testid="bank-account-number" value={form.bank_account_number} onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })} placeholder="Nomor rekening" style={{ ...inp, fontFamily: "'IBM Plex Mono', monospace" }} />
            <input data-testid="bank-account-name" value={form.bank_account_name} onChange={(e) => setForm({ ...form, bank_account_name: e.target.value })} placeholder="Nama pemilik rekening" style={inp} />
            <p style={{ margin: "6px 0 0", fontSize: 12, color: "#6E6559" }}>Ditampilkan otomatis sebagai opsi selain QRIS di halaman checkout Pemesan.</p>
          </div>
        </div>
        <button data-testid="save-payment" onClick={savePayment} disabled={saving || !qrisValid} style={{ marginTop: 16, padding: "12px 24px", background: qrisValid ? "#1A17E0" : "rgba(38,34,30,0.24)", color: "#fff", border: "none", borderRadius: 4, fontWeight: 700, cursor: qrisValid && !saving ? "pointer" : "not-allowed", fontSize: 14 }}>{saving ? "Menyimpan…" : "Simpan Info Pembayaran"}</button>
      </div>

      {/* READ-ONLY MENU */}
      <div>
        <div style={{ padding: "12px 16px", border: "1px solid #E8823A", borderRadius: 8, background: "#FFF5EC", color: "#26221E", marginBottom: 16, fontSize: 13 }}>
          Ini tampilan menu <strong>seperti yang dilihat Pemesan</strong> di kantormu — hanya untuk melihat.
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[["all", "Semua"], ["nasi", "Makanan"], ["minuman", "Minuman"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} style={{ padding: "8px 14px", borderRadius: 999, border: "1px solid " + (filter === k ? "#1A17E0" : "rgba(38,34,30,0.18)"), background: filter === k ? "#1A17E0" : "#fff", color: filter === k ? "#fff" : "#26221E", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{l}</button>
          ))}
        </div>
        <p className="font-mono-c" style={{ fontSize: 12, color: "#6E6559", margin: "0 0 12px" }}>{list.length} MENU TAYANG DI EMAIL KANTORMU</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
          {list.map((d) => {
            const soldOut = d.portions <= 0;
            const own = d.cook_user_id === userId;
            return (
              <div key={d.dish_id} style={{ background: "#fff", border: "1px solid rgba(38,34,30,0.10)", borderRadius: 14, overflow: "hidden", opacity: soldOut ? 0.6 : 1 }}>
                <div style={{ position: "relative" }}>
                  <img src={photoSrc(d.photo_url)} alt={d.name} style={{ width: "100%", height: 160, objectFit: "cover", filter: soldOut ? "grayscale(1)" : "none" }} />
                  {own && <span style={{ position: "absolute", top: 10, left: 10, background: "#1A17E0", color: "#fff", fontSize: 11, padding: "4px 8px", borderRadius: 4, fontWeight: 700 }}>Menumu</span>}
                </div>
                <div style={{ padding: 14 }}>
                  <strong>{d.name}</strong>
                  <div style={{ fontSize: 12, color: "#6E6559", marginTop: 2 }}>oleh {d.cook_name}</div>
                  <div className="font-mono-c" style={{ fontSize: 11, color: "#6E6559", marginTop: 4 }}>{d.category}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                    <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700 }}>{rupiah(d.price)}</span>
                    <span style={{ fontSize: 12, color: soldOut ? "#B3261E" : "#2F7A3D", fontWeight: 700 }}>{soldOut ? "Habis" : `sisa ${d.portions} porsi`}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Tab 4: Jadwal & Titik Ambil ---------------- */
function JadwalTab({ profile, setProfile, pickups, setPickups }) {
  const [monthOffset, setMonthOffset] = useState(0);

  const saveProfile = async (patch) => {
    const { data } = await api.put("/my/seller-profile", { ...profile, ...patch });
    setProfile(data);
  };

  const addPickup = async () => {
    if (pickups.length >= 3) return toast.error("Maksimal 3 titik ambil");
    const { data } = await api.post("/my/pickup-points", { name: "Titik baru", note: "", active: true });
    setPickups((p) => [...p, data]);
  };
  const savePickup = async (p, patch) => {
    const { data } = await api.put(`/my/pickup-points/${p.id}`, { ...p, ...patch });
    setPickups((prev) => prev.map((x) => (x.id === p.id ? data : x)));
  };

  const today = new Date();
  const shown = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const monthLabel = shown.toLocaleDateString("id-ID", { month: "long", year: "numeric" });
  const startDow = (shown.getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(shown.getFullYear(), shown.getMonth() + 1, 0).getDate();
  const closedSet = new Set(profile.closed_dates || []);
  const toggleClosed = (iso) => {
    const next = closedSet.has(iso) ? [...closedSet].filter((d) => d !== iso) : [...closedSet, iso];
    saveProfile({ closed_dates: next });
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20 }}>
      <div style={{ background: "#fff", border: "1px solid rgba(38,34,30,0.10)", borderRadius: 14, padding: 20 }}>
        <h3 style={{ margin: "0 0 4px", fontFamily: "'Bitter', Georgia, serif", fontWeight: 800 }}>Titik Ambil</h3>
        <p style={{ fontSize: 12, color: "#6E6559", margin: "0 0 12px" }}>Maksimal 3 titik. Ganti nama/aktifkan sesuai kebutuhan.</p>
        {pickups.map((p) => (
          <div key={p.id} data-testid={`pickup-${p.id}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, background: p.active ? "#FBEFD3" : "transparent", border: p.active ? "1px solid #1A17E0" : "1px solid rgba(38,34,30,0.10)", borderRadius: 8, marginBottom: 8 }}>
            <input defaultValue={p.name} onBlur={(e) => savePickup(p, { name: e.target.value })} style={{ flex: 1, border: "none", background: "transparent", fontWeight: 700, fontSize: 14 }} />
            <input defaultValue={p.note} onBlur={(e) => savePickup(p, { note: e.target.value })} placeholder="catatan" style={{ flex: 1, border: "none", background: "transparent", fontSize: 12, color: "#6E6559" }} />
            <button onClick={() => savePickup(p, { active: !p.active })} style={{ padding: "4px 10px", borderRadius: 999, border: "1px solid " + (p.active ? "#1A17E0" : "rgba(38,34,30,0.24)"), background: p.active ? "#1A17E0" : "#fff", color: p.active ? "#fff" : "#26221E", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>{p.active ? "ON" : "OFF"}</button>
          </div>
        ))}
        {pickups.length < 3 && <button data-testid="add-pickup" onClick={addPickup} style={{ marginTop: 8, padding: "10px 14px", background: "transparent", border: "1px dashed rgba(38,34,30,0.30)", borderRadius: 8, cursor: "pointer", width: "100%", fontSize: 13 }}>+ Tambah titik ambil</button>}
        <p style={{ fontSize: 11, color: "#9A9084", marginTop: 8 }}>{pickups.length}/3 aktif</p>
      </div>

      <div style={{ background: "#26221E", color: "#FBEFD3", borderRadius: 14, padding: 20 }}>
        <h3 style={{ margin: "0 0 12px", fontFamily: "'Bitter', Georgia, serif", fontWeight: 800 }}>Jam Buka & Tutup Order</h3>
        <div style={{ marginBottom: 14 }}>
          <p className="font-mono-c" style={{ fontSize: 11, letterSpacing: "0.12em", color: "rgba(251,239,211,0.7)", margin: "0 0 6px" }}>BUKA ORDER</p>
          <div style={{ display: "flex", gap: 6 }}>
            {["06.00", "07.00", "09.00"].map((h) => (
              <button key={h} data-testid={`open-${h}`} onClick={() => saveProfile({ open_hour: h })} style={{ padding: "8px 14px", borderRadius: 4, border: "none", background: profile.open_hour === h ? "#F0B429" : "rgba(255,255,255,0.10)", color: profile.open_hour === h ? "#26221E" : "#FBEFD3", fontWeight: 700, cursor: "pointer" }}>{h}</button>
            ))}
          </div>
        </div>
        <div>
          <p className="font-mono-c" style={{ fontSize: 11, letterSpacing: "0.12em", color: "rgba(251,239,211,0.7)", margin: "0 0 6px" }}>TUTUP ORDER</p>
          <div style={{ display: "flex", gap: 6 }}>
            {["20.00", "22.00", "23.00"].map((h) => (
              <button key={h} data-testid={`close-${h}`} onClick={() => saveProfile({ close_hour: h })} style={{ padding: "8px 14px", borderRadius: 4, border: "none", background: profile.close_hour === h ? "#F0B429" : "rgba(255,255,255,0.10)", color: profile.close_hour === h ? "#26221E" : "#FBEFD3", fontWeight: 700, cursor: "pointer" }}>{h}</button>
            ))}
          </div>
        </div>
        <p style={{ marginTop: 16, fontSize: 13, color: "rgba(251,239,211,0.8)" }}>Pesanan dibuka jam <strong>{profile.open_hour}</strong> dan ditutup jam <strong>{profile.close_hour}</strong> untuk servis besok.</p>
      </div>

      <div style={{ background: "#fff", border: "1px solid rgba(38,34,30,0.10)", borderRadius: 14, padding: 20, gridColumn: "1/-1" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontFamily: "'Bitter', Georgia, serif", fontWeight: 800 }}>Kalender Jualan — {monthLabel}</h3>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setMonthOffset((n) => n - 1)} style={btnSm}>‹</button>
            <button onClick={() => setMonthOffset(0)} style={btnSm}>Hari ini</button>
            <button onClick={() => setMonthOffset((n) => n + 1)} style={btnSm}>›</button>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, fontSize: 11, color: "#6E6559", marginBottom: 6 }}>
          {["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"].map((d) => <div key={d} style={{ padding: 6, textAlign: "center", fontWeight: 700 }}>{d}</div>)}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
          {Array.from({ length: startDow }, (_, i) => <div key={"e" + i} />)}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const d = i + 1;
            const iso = new Date(shown.getFullYear(), shown.getMonth(), d).toISOString().slice(0, 10);
            const closed = closedSet.has(iso);
            const isWeekend = (i + startDow) % 7 >= 5;
            const bg = closed ? "rgba(38,34,30,0.10)" : isWeekend ? "#F0B429" : "#1A17E0";
            const color = closed ? "#9A9084" : "#fff";
            return (
              <button key={d} data-testid={`cal-${iso}`} onClick={() => toggleClosed(iso)} style={{ padding: "12px 0", background: bg, color, border: "none", borderRadius: 6, fontWeight: 700, cursor: "pointer", textDecoration: closed ? "line-through" : "none" }}>{d}</button>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 12, marginTop: 12, fontSize: 11, color: "#6E6559" }}>
          <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#1A17E0", borderRadius: 3, marginRight: 4 }} />Buka</span>
          <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#F0B429", borderRadius: 3, marginRight: 4 }} />Akhir pekan</span>
          <span><span style={{ display: "inline-block", width: 10, height: 10, background: "rgba(38,34,30,0.10)", borderRadius: 3, marginRight: 4 }} />Libur</span>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Tab 5: Performa ---------------- */
function PerformaTab({ analytics, setAnalytics }) {
  useEffect(() => { if (!analytics) api.get("/my/analytics").then(({ data }) => setAnalytics(data)); }, [analytics, setAnalytics]);
  if (!analytics) return <div style={emptyBox}>Memuat analitik…</div>;
  const max = Math.max(...analytics.chart.map((c) => c.total), 1);
  const peakIdx = analytics.chart.findIndex((c) => c.total === max);
  const maxMenu = analytics.top_menus[0]?.count || 1;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
        <Kpi label="Omzet 14 hari"          value={rupiah(analytics.total_revenue)} />
        <Kpi label="Porsi terjual"           value={analytics.total_portions} />
        <Kpi label="Rata-rata per hari"      value={rupiah(analytics.avg_daily)} />
        <Kpi label="Nilai order rata-rata"   value={rupiah(analytics.avg_order)} />
      </div>
      <div style={{ background: "#fff", border: "1px solid rgba(38,34,30,0.10)", borderRadius: 14, padding: 20 }}>
        <h3 style={{ margin: "0 0 14px", fontFamily: "'Bitter', Georgia, serif", fontWeight: 800 }}>Omzet 14 hari terakhir</h3>
        <div style={{ display: "flex", gap: 6, alignItems: "flex-end", height: 160 }}>
          {analytics.chart.map((c, i) => {
            const h = c.total ? Math.max(6, (c.total / max) * 140) : 3;
            return (
              <div key={c.date} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                <div style={{ width: "100%", height: h, background: i === peakIdx ? "#E8823A" : c.total ? "#1A17E0" : "rgba(38,34,30,0.10)", borderRadius: "4px 4px 0 0" }} />
                <span style={{ fontSize: 10, color: "#9A9084" }}>{c.date.slice(-2)}</span>
              </div>
            );
          })}
        </div>
        <p style={{ marginTop: 12, fontFamily: "'IBM Plex Mono', monospace", fontSize: 13 }}>Total {rupiah(analytics.total_revenue)}</p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20 }}>
        <div style={{ background: "#fff", border: "1px solid rgba(38,34,30,0.10)", borderRadius: 14, padding: 20 }}>
          <h3 style={{ margin: "0 0 14px", fontFamily: "'Bitter', Georgia, serif", fontWeight: 800 }}>Menu Paling Sering Dipesan</h3>
          {analytics.top_menus.length === 0 && <p style={{ color: "#9A9084" }}>Belum ada penjualan lunas.</p>}
          {analytics.top_menus.map((m, i) => (
            <div key={m.name} style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}><span>#{i + 1} {m.name}</span><strong>{m.count}</strong></div>
              <div style={{ height: 6, background: "rgba(38,34,30,0.08)", borderRadius: 999, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${(m.count / maxMenu) * 100}%`, background: i === 0 ? "#E8823A" : "#1A17E0" }} />
              </div>
            </div>
          ))}
        </div>
        <div style={{ background: "#fff", border: "1px solid rgba(38,34,30,0.10)", borderRadius: 14, padding: 20 }}>
          <h3 style={{ margin: "0 0 14px", fontFamily: "'Bitter', Georgia, serif", fontWeight: 800 }}>Pelanggan Paling Setia</h3>
          {analytics.top_buyers.length === 0 && <p style={{ color: "#9A9084" }}>Belum ada pembeli tetap.</p>}
          {analytics.top_buyers.map((b, i) => (
            <div key={b.name + i} style={{ display: "grid", gridTemplateColumns: "28px 1fr auto", gap: 10, alignItems: "center", padding: "8px 0", borderBottom: "1px solid rgba(38,34,30,0.06)" }}>
              <span style={{ width: 26, height: 26, borderRadius: 6, background: i === 0 ? "#F0B429" : "rgba(38,34,30,0.08)", display: "grid", placeItems: "center", fontWeight: 700, fontSize: 12 }}>#{i + 1}</span>
              <div><strong style={{ fontSize: 13 }}>{b.name}</strong><div style={{ fontSize: 11, color: "#9A9084" }}>{b.count} pesanan</div></div>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, fontWeight: 700 }}>{rupiah(b.total)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Tab 6: Belanja Bahan AI ---------------- */
function BelanjaTab({ shopping, setShopping }) {
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try { const { data } = await api.get("/my/shopping-list"); setShopping(data); }
    catch (e) { toast.error("Gagal menghitung"); }
    finally { setLoading(false); }
  };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 340px) minmax(0, 1fr)", gap: 20 }}>
      <div style={{ background: "#1A17E0", color: "#fff", borderRadius: 14, padding: 20 }}>
        <p className="font-mono-c" style={{ fontSize: 11, letterSpacing: "0.14em", opacity: 0.7, margin: "0 0 8px" }}>PESANAN TERKUNCI</p>
        <h3 style={{ margin: "0 0 12px", fontFamily: "'Bitter', Georgia, serif", fontWeight: 800 }}>{shopping?.menus?.length || 0} menu · {shopping?.service_date || "belum ada"}</h3>
        {shopping?.menus?.map((m) => <div key={m.name} style={{ fontSize: 13, marginBottom: 4 }}>{m.portions}× {m.name}</div>)}
        <button data-testid="hitung-bahan" onClick={run} disabled={loading} style={{ marginTop: 14, width: "100%", padding: "12px", background: "#F0B429", color: "#26221E", border: "none", borderRadius: 4, fontWeight: 700, cursor: "pointer" }}>{loading ? "Menghitung…" : shopping ? "Hitung Ulang Daftar Belanja" : "Hitung Bahan dengan AI"}</button>
      </div>
      <div>
        {!shopping && <div style={emptyBox}>Klik "Hitung Bahan" untuk mengeluarkan daftar belanja dari pesanan yang sudah terkunci.</div>}
        {shopping && shopping.ingredients?.length > 0 && (
          <>
            <div style={{ background: "#fff", border: "1px solid rgba(38,34,30,0.10)", borderRadius: 14, overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", padding: "12px 16px", background: "rgba(38,34,30,0.04)", fontSize: 12, fontWeight: 700, color: "#6E6559", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                <span>Bahan</span><span>Total</span><span>Beli</span><span>Estimasi</span>
              </div>
              {shopping.ingredients.map((r) => (
                <div key={`${r.name}-${r.unit || ""}`} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", padding: "12px 16px", borderTop: "1px solid rgba(38,34,30,0.06)", fontSize: 13 }}>
                  <span>{r.name}</span><span>{r.need}</span><span>{r.buy}</span><span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{rupiah(r.cost)}</span>
                </div>
              ))}
              <div style={{ padding: "12px 16px", background: "rgba(38,34,30,0.06)", display: "flex", justifyContent: "space-between", fontWeight: 800 }}>
                <span>Total belanja</span><span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{rupiah(shopping.estimated_cost)}</span>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginTop: 16 }}>
              <Kpi label="Estimasi omzet besok" value={rupiah(shopping.estimated_revenue)} />
              <Kpi label="Biaya bahan"          value={rupiah(shopping.estimated_cost)} />
              <div style={{ background: "#26221E", color: "#FBEFD3", padding: 16, borderRadius: 14 }}>
                <div style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.08em" }}>PERKIRAAN LABA</div>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 22, fontWeight: 700, marginTop: 6, color: "#F0B429" }}>{rupiah(shopping.estimated_profit)}</div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- Tab 7: Tagihan (real FeeInvoice) ---------------- */
function TagihanTab() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [setorOpen, setSetorOpen] = useState(null); // invoice_id currently in setor modal
  const [proofFile, setProofFile] = useState(null);
  const [setorNote, setSetorNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const load = () => api.get("/my/fee-invoices").then(({ data }) => setInvoices(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const submitSetor = async () => {
    if (!proofFile) return toast.error("Unggah bukti transfer dulu");
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", proofFile);
      const { data: up } = await api.post("/upload", fd);
      await api.post(`/my/fee-invoices/${setorOpen}/setor`, { proof_url: up.photo_url, note: setorNote });
      toast.success("Setor tercatat · admin akan verifikasi 1×24 jam");
      setSetorOpen(null); setProofFile(null); setSetorNote(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal upload bukti"); }
    finally { setUploading(false); }
  };
  if (loading) return <div style={emptyBox}>Memuat tagihan…</div>;
  const outstanding = invoices.filter((i) => i.status !== "lunas");
  const total_outstanding = outstanding.reduce((s, i) => s + i.fee_amount, 0);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        <Kpi label="Tagihan aktif" value={outstanding.length} color="#E8823A" />
        <Kpi label="Total belum setor" value={rupiah(total_outstanding)} color="#1A17E0" />
        <Kpi label="Riwayat lunas" value={invoices.filter((i) => i.status === "lunas").length} color="#2F7A3D" />
      </div>
      {invoices.length === 0 && <div style={emptyBox}>Belum ada tagihan. Cron generator jalan otomatis tiap tanggal 25 jam 09.00 WIB.</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {invoices.map((inv) => (
          <div key={inv.invoice_id} data-testid={`invoice-${inv.invoice_id}`} style={{ background: "#fff", border: "1px solid rgba(38,34,30,0.10)", borderRadius: 14, padding: 16, display: "grid", gridTemplateColumns: "auto 1fr auto auto", gap: 16, alignItems: "center" }}>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: "#6E6559" }}>{inv.invoice_id}</span>
            <div>
              <strong style={{ fontSize: 14 }}>Periode {inv.period_start} → {inv.period_end}</strong>
              <div style={{ fontSize: 12, color: "#6E6559", marginTop: 2 }}>{inv.orders_count} pesanan · omzet {rupiah(inv.gross_revenue)}</div>
              {inv.proof_url && <a href={photoSrc(inv.proof_url)} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "#1A17E0" }}>Lihat bukti setor</a>}
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, fontSize: 16, color: "#1A17E0" }}>{rupiah(inv.fee_amount)}</div>
              <div className="font-mono-c" style={{ fontSize: 10, color: "#9A9084" }}>5% platform fee</div>
            </div>
            {inv.status === "belum_setor" && <button data-testid={`setor-${inv.invoice_id}`} onClick={() => setSetorOpen(inv.invoice_id)} style={{ padding: "10px 14px", background: "#E8823A", color: "#fff", border: "none", borderRadius: 4, fontWeight: 700, cursor: "pointer", fontSize: 12 }}>Saya Sudah Setor</button>}
            {inv.status === "menunggu_verifikasi" && <span className="font-mono-c" style={{ padding: "6px 10px", borderRadius: 999, background: "#FEF6D9", color: "#8A6300", fontSize: 11, fontWeight: 700 }}>MENUNGGU VERIFIKASI</span>}
            {inv.status === "lunas" && <span className="font-mono-c" style={{ padding: "6px 10px", borderRadius: 999, background: "#E7F6EC", color: "#2F7A3D", fontSize: 11, fontWeight: 700 }}>LUNAS</span>}
          </div>
        ))}
      </div>

      {setorOpen && (
        <div data-testid="setor-modal" onClick={() => !uploading && setSetorOpen(null)} style={{ position: "fixed", inset: 0, background: "rgba(38,34,30,0.5)", display: "grid", placeItems: "center", zIndex: 200 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", padding: 24, borderRadius: 14, width: "min(440px, 92vw)" }}>
            <h3 style={{ fontFamily: "'Bitter', Georgia, serif", fontWeight: 800, margin: "0 0 6px" }}>Bukti Setor</h3>
            <p style={{ fontSize: 12, color: "#6E6559", margin: "0 0 14px" }}>Unggah foto/screenshot bukti transfer ke QRIS Cookingin. Admin verifikasi maksimal 1×24 jam.</p>
            <label style={{ display: "block", padding: 20, border: "2px dashed rgba(38,34,30,0.24)", borderRadius: 10, textAlign: "center", cursor: "pointer", marginBottom: 12 }}>
              {proofFile ? <span style={{ fontSize: 13, color: "#2F7A3D", fontWeight: 700 }}>✓ {proofFile.name}</span> : <span style={{ color: "#9A9084", fontSize: 13 }}>Klik untuk pilih file (JPG/PNG/PDF)</span>}
              <input data-testid="proof-file" type="file" accept="image/*,.pdf" hidden onChange={(e) => setProofFile(e.target.files[0])} />
            </label>
            <textarea data-testid="setor-note" value={setorNote} onChange={(e) => setSetorNote(e.target.value)} placeholder="Catatan opsional (nomor referensi, dll.)" maxLength={280} style={{ ...inp, minHeight: 60, resize: "vertical" }} />
            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
              <button onClick={() => setSetorOpen(null)} disabled={uploading} style={{ flex: 1, ...btnSm, padding: "12px", fontSize: 14 }}>Batal</button>
              <button data-testid="setor-submit" onClick={submitSetor} disabled={uploading || !proofFile} style={{ flex: 2, padding: "12px", background: proofFile ? "#1A17E0" : "rgba(38,34,30,0.24)", color: "#fff", border: "none", borderRadius: 4, fontWeight: 700, cursor: proofFile && !uploading ? "pointer" : "not-allowed", fontSize: 14 }}>{uploading ? "Mengunggah…" : "Kirim Bukti"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Tab 8: Admin (contact messages + fee invoices) ---------------- */
function AdminTab() {
  const [msgs, setMsgs] = useState([]);
  const [invs, setInvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([api.get("/admin/contact-messages"), api.get("/admin/fee-invoices")]);
      setMsgs(a.data); setInvs(b.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const markRead = async (id) => { await api.post(`/admin/contact-messages/${id}/mark-read`); load(); };
  const verify = async (id) => { await api.post(`/admin/fee-invoices/${id}/verify`); load(); };
  const generate = async () => { const { data } = await api.post("/admin/generate-fee-invoices"); toast.success(`Dibuat ${data.invoices_created} tagihan untuk periode ${data.period}`); load(); };
  if (loading) return <div style={emptyBox}>Memuat data admin…</div>;
  const unread = msgs.filter((m) => !m.read_at).length;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ background: "#26221E", color: "#FBEFD3", borderRadius: 14, padding: 20, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontFamily: "'Bitter', Georgia, serif", fontWeight: 800 }}>Panel Admin Cookingin</h3>
          <p style={{ margin: "4px 0 0", fontSize: 13, opacity: 0.8 }}>{unread} pesan belum dibaca · {invs.filter((i) => i.status === "menunggu_verifikasi").length} setor menunggu verifikasi</p>
        </div>
        <button data-testid="admin-generate-invoices" onClick={generate} style={{ padding: "10px 16px", background: "#F0B429", color: "#26221E", border: "none", borderRadius: 4, fontWeight: 700, cursor: "pointer" }}>Generate Tagihan Sekarang</button>
      </div>
      <section>
        <h4 style={{ fontFamily: "'Bitter', Georgia, serif", fontWeight: 800, margin: "0 0 10px" }}>Inbox Kontak · {msgs.length}</h4>
        {msgs.length === 0 && <div style={emptyBox}>Belum ada pesan.</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {msgs.map((m) => (
            <div key={m.contact_id} data-testid={`msg-${m.contact_id}`} style={{ background: m.read_at ? "#fff" : "#FFF5EC", border: "1px solid " + (m.read_at ? "rgba(38,34,30,0.10)" : "#E8823A"), borderRadius: 14, padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline", marginBottom: 6 }}>
                <strong style={{ fontSize: 14 }}>{m.name}</strong>
                <span className="font-mono-c" style={{ fontSize: 10, color: "#9A9084" }}>{m.created_at_iso?.slice(0, 16)?.replace("T", " ")}</span>
              </div>
              <div className="font-mono-c" style={{ fontSize: 11, color: "#1A17E0", marginBottom: 6 }}>{m.email}</div>
              <p style={{ fontSize: 13, margin: "0 0 8px", whiteSpace: "pre-wrap" }}>{m.message}</p>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <a href={`mailto:${m.email}`} style={{ padding: "6px 12px", background: "#1A17E0", color: "#fff", borderRadius: 4, fontSize: 12, fontWeight: 700, textDecoration: "none" }}>Balas via Email</a>
                {!m.read_at && <button data-testid={`mark-${m.contact_id}`} onClick={() => markRead(m.contact_id)} style={{ ...btnSm, background: "transparent" }}>Tandai Sudah Dibaca</button>}
              </div>
            </div>
          ))}
        </div>
      </section>
      <section>
        <h4 style={{ fontFamily: "'Bitter', Georgia, serif", fontWeight: 800, margin: "0 0 10px" }}>Verifikasi Setor · {invs.filter((i) => i.status === "menunggu_verifikasi").length}</h4>
        {invs.length === 0 && <div style={emptyBox}>Belum ada tagihan.</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {invs.map((inv) => (
            <div key={inv.invoice_id} style={{ background: "#fff", border: "1px solid rgba(38,34,30,0.10)", borderRadius: 14, padding: 16, display: "grid", gridTemplateColumns: "auto 1fr auto auto", gap: 16, alignItems: "center" }}>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: "#6E6559" }}>{inv.invoice_id}</span>
              <div>
                <strong style={{ fontSize: 13 }}>{inv.cook_name}</strong>
                <div style={{ fontSize: 11, color: "#6E6559" }}>{inv.period_start} → {inv.period_end} · {inv.orders_count} pesanan</div>
              </div>
              <div style={{ textAlign: "right", fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700 }}>
                {rupiah(inv.fee_amount)}
                {inv.proof_url && <div><a href={photoSrc(inv.proof_url)} target="_blank" rel="noreferrer" style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: 10, color: "#1A17E0", fontWeight: 700 }}>Lihat bukti</a></div>}
              </div>
              {inv.status === "menunggu_verifikasi" ? (
                <button data-testid={`verify-${inv.invoice_id}`} onClick={() => verify(inv.invoice_id)} style={{ padding: "8px 12px", background: "#2F7A3D", color: "#fff", border: "none", borderRadius: 4, fontWeight: 700, cursor: "pointer", fontSize: 12 }}>Verifikasi Lunas</button>
              ) : (
                <span className="font-mono-c" style={{ fontSize: 11, color: inv.status === "lunas" ? "#2F7A3D" : "#9A9084", fontWeight: 700 }}>{inv.status.toUpperCase()}</span>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/* ---------------- shared bits ---------------- */
const inp = { width: "100%", padding: "10px 12px", border: "1px solid rgba(38,34,30,0.18)", borderRadius: 4, marginBottom: 10, fontSize: 14, fontFamily: "inherit" };
const btnStep = { width: 30, height: 30, borderRadius: 4, border: "1px solid rgba(38,34,30,0.24)", background: "#fff", cursor: "pointer" };
const btnSm = { padding: "6px 10px", border: "1px solid rgba(38,34,30,0.18)", borderRadius: 4, background: "#fff", cursor: "pointer", fontSize: 12 };
const emptyBox = { padding: 30, textAlign: "center", background: "#fff", border: "1px dashed rgba(38,34,30,0.24)", borderRadius: 14, color: "#6E6559", fontSize: 14 };

function Kpi({ label, value, color, sub }) {
  return (
    <div style={{ background: "#fff", padding: 16, borderRadius: 14, border: "1px solid rgba(38,34,30,0.10)" }}>
      <div style={{ fontSize: 11, letterSpacing: "0.08em", color: "#6E6559", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 22, fontWeight: 700, marginTop: 6, color: color || "#26221E" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#9A9084", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
function Row({ label, value, color, strong }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderTop: "1px solid rgba(38,34,30,0.08)", fontWeight: strong ? 700 : 400 }}>
      <span style={{ color: "#6E6559", fontSize: 14 }}>{label}</span>
      <span style={{ fontFamily: "'IBM Plex Mono', monospace", color: color || "#26221E", fontSize: strong ? 18 : 14 }}>{value}</span>
    </div>
  );
}
